import { useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Skeleton, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, LineChart, Link2, Pencil, Unlink } from "lucide-react";

import { Badge } from "../../../components/Badge";
import { Button } from "../../../components/Button";
import { ConfirmModal } from "../../../components/ConfirmModal";
import { GoogleIcon } from "../../../components/icons/GoogleIcon";
import { GoogleAnalyticsIcon } from "../../../components/icons/GoogleAnalyticsIcon";
import { useAuth } from "../../../components/contexts/AuthContext";
import { getSeoProvider } from "@/utils/agencyFeatures";
import {
  deleteSearchConsoleSite,
  getClientAnalyticsLink,
  getClientSearchConsoleLink,
  unlinkAnalyticsProperty,
} from "../../../services/integrationsService";
import { getClientSeRankingLink, unlinkSeRankingSite } from "../../../services/seRankingService";
import { PUBLISHABLE_PLATFORMS, PlatformIcon, platformLabel } from "../../ContentGenerator/workflow/platformMeta";
import { ConnectGoogleAnalyticsModal } from "../metrics/ConnectGoogleAnalyticsModal";
import { ConnectSearchConsoleModal, searchConsoleSiteLabel } from "../metrics/ConnectSearchConsoleModal";
import { SeoConnectModal } from "../seoSetup/SeoConnectModal";
import { useSeoSetupRow } from "../seoSetup/hooks";

const STATUS_META = {
  connected: { label: "Conectada", color: "done" },
  needs_reconnect: { label: "Hay que reconectar", color: "pending" },
  disconnected: { label: "No conectada", color: "muted" },
  // SEO setup stepper, from this browser's localStorage only.
  setup_pending: { label: "Configuración pendiente", color: "pending" },
};

const METRICS = [
  {
    key: "ga4",
    label: "Google Analytics",
    Icon: () => <GoogleAnalyticsIcon size={20} />,
    fetchLink: getClientAnalyticsLink,
    unlink: unlinkAnalyticsProperty,
    describe: (link) => link.property_name || link.property_id,
    Modal: ConnectGoogleAnalyticsModal,
  },
  {
    key: "search_console",
    label: "Search Console",
    Icon: () => <GoogleIcon size={18} />,
    fetchLink: getClientSearchConsoleLink,
    unlink: deleteSearchConsoleSite,
    describe: searchConsoleSiteLabel,
    Modal: ConnectSearchConsoleModal,
  },
  {
    key: "seranking",
    label: "SE Ranking",
    Icon: () => <LineChart size={18} color="#2563EB" />,
    fetchLink: getClientSeRankingLink,
    unlink: unlinkSeRankingSite,
    describe: (link) => link.site_title || link.site_domain,
    Modal: SeoConnectModal,
  },
];

const ROW_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 1.25,
  px: 2,
  py: 1.75,
  backgroundColor: "#fff",
};

function RowHeader({ icon, label, status, detail, detailColor, loading, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {icon}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" width={90} height={24} />
          ) : (
            <Badge color={status.color}>{status.label}</Badge>
          )}
        </Box>
        {detail && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: detailColor || "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {detail}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
}

function IconBox({ children }) {
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        minWidth: 36,
        borderRadius: "10px",
        backgroundColor: "#F3F4F6",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </Box>
  );
}

// Every row's button the same width, whatever its label.
const ACTION_BUTTON_SX = { minWidth: 96 };

function MetricRow({ integration, clientId, busy, onConnect, onDisconnect, seoSetup }) {
  const { interceptorReady } = useAuth();
  const { data: link, isLoading } = useQuery({
    queryKey: ["client-connection", integration.key, clientId],
    queryFn: () => integration.fetchLink(clientId),
    enabled: interceptorReady && Boolean(clientId),
  });
  const connected = Boolean(link);
  const setupStatus = useSeoSetupRow(clientId, {
    enabled: Boolean(seoSetup),
    linked: connected || isLoading,
  });
  const status = connected
    ? STATUS_META.connected
    : STATUS_META[`setup_${setupStatus}`] || STATUS_META.disconnected;
  return (
    <Box sx={ROW_SX}>
      <RowHeader
        icon={
          <IconBox>
            <integration.Icon />
          </IconBox>
        }
        label={integration.label}
        loading={isLoading}
        status={status}
        detail={connected ? integration.describe(link) : ""}
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {connected && link.managed ? (
              // Markezy created it: it can be edited but never unlinked.
              <Button size="xs" variant="outline" onClick={() => onConnect(integration, link)} sx={ACTION_BUTTON_SX}>
                <Pencil size={12} />
                Editar
              </Button>
            ) : connected ? (
              <Button size="xs" variant="outline" onClick={() => onDisconnect(integration, link)} disabled={busy} sx={ACTION_BUTTON_SX}>
                {busy ? <CircularProgress size={12} /> : <Unlink size={12} />}
                Desconectar
              </Button>
            ) : (
              <Button size="xs" onClick={() => onConnect(integration)} disabled={isLoading} sx={ACTION_BUTTON_SX}>
                <Link2 size={12} />
                {setupStatus === "pending" ? "Continuar" : "Conectar"}
              </Button>
            )}
            {/* Same width as the URL toggle of the network rows, to align the buttons. */}
            <Box sx={{ width: 34 }} />
          </Box>
        }
      />
    </Box>
  );
}

MetricRow.propTypes = {
  integration: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    Icon: PropTypes.elementType.isRequired,
    fetchLink: PropTypes.func.isRequired,
    describe: PropTypes.func.isRequired,
  }).isRequired,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  busy: PropTypes.bool,
  onConnect: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func.isRequired,
  // Only for the SEO row.
  seoSetup: PropTypes.shape({ modalOpen: PropTypes.bool }),
};

function SocialRow({ platform, social, urlField }) {
  const [showUrl, setShowUrl] = useState(false);
  const account = social.accounts[platform];
  const isConnected = account?.status === "connected";
  const status = STATUS_META[account?.status] || STATUS_META.disconnected;
  const isBusy = social.busy === platform;
  const error = !isConnected && account?.last_error;
  const name = isConnected
    ? [account.display_name || account.username, account.channel_name].filter(Boolean).join(" · ")
    : "";

  return (
    <Box sx={{ ...ROW_SX, p: 0, gap: 0 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={showUrl}
        onClick={() => setShowUrl((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          setShowUrl((prev) => !prev);
        }}
        sx={{
          px: 2,
          py: 1.75,
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <RowHeader
          icon={<PlatformIcon platform={platform} size={36} />}
          label={platformLabel(platform)}
          loading={social.isLoading}
          status={status}
          detail={error || name}
          detailColor={error ? "#B45309" : undefined}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {isConnected ? (
                  <Button size="xs" variant="outline" onClick={() => social.disconnect(platform)} disabled={Boolean(social.busy)} sx={ACTION_BUTTON_SX}>
                    {isBusy ? <CircularProgress size={12} /> : <Unlink size={12} />}
                    Desconectar
                  </Button>
                ) : (
                  <Button size="xs" onClick={() => social.connect(platform)} disabled={Boolean(social.busy) || social.isLoading} sx={ACTION_BUTTON_SX}>
                    {isBusy ? <CircularProgress size={12} sx={{ color: "#fff" }} /> : <Link2 size={12} />}
                    {account?.status === "needs_reconnect" ? "Reconectar" : "Conectar"}
                  </Button>
                )}
              </Box>
              <Box sx={{ width: 34, display: "flex", justifyContent: "center", color: "text.secondary" }}>
                <ChevronDown
                  size={18}
                  style={{ transform: showUrl ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
                />
              </Box>
            </Box>
          }
        />
      </Box>
      {showUrl && <Box sx={{ px: 2, pb: 1.75, pl: { sm: 8 } }}>{urlField}</Box>}
    </Box>
  );
}

/**
 * Connections of a client (internal users only): metric sources and the networks
 * contents are published to, in one list. `renderUrlField(platform)` renders the
 * optional profile URL of each network (collapsed), stored as a digital asset.
 */
export function ClientConnections({ clientId, social, renderUrlField, onSuccess, onError }) {
  const { user } = useAuth();
  const seoLabel = getSeoProvider(user).label;
  const metrics = METRICS.map((integration) =>
    integration.key === "seranking" ? { ...integration, label: seoLabel } : integration
  );
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(null); // integration whose dialog is open, plus its `currentLink`
  const [confirmUnlink, setConfirmUnlink] = useState(null); // { integration, link }
  const [unlinking, setUnlinking] = useState(null); // integration key

  const setLink = (key, link) => queryClient.setQueryData(["client-connection", key, clientId], link);
  const Modal = connecting?.Modal;

  const unlink = async () => {
    const { integration, link } = confirmUnlink;
    setConfirmUnlink(null);
    setUnlinking(integration.key);
    try {
      await integration.unlink(link.id);
      setLink(integration.key, null);
      onSuccess?.(`${integration.label} desconectado`);
    } catch {
      onError?.(`No se ha podido desconectar ${integration.label}`);
    } finally {
      setUnlinking(null);
    }
  };

  return (
    <>
      {/* The 1px gap over the grey background draws the dividers. */}
      <Box
        sx={{
          display: "grid",
          // Two columns only when each one gets at least 420px.
          gridTemplateColumns: "repeat(auto-fit, minmax(max(420px, calc(50% - 1px)), 1fr))",
          gap: "1px",
          backgroundColor: "#F1F5F9",
          border: "1px solid #E8ECF2",
          borderRadius: "12px",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {metrics.map((integration) => (
          <MetricRow
            key={integration.key}
            integration={integration}
            clientId={clientId}
            busy={unlinking === integration.key}
            onConnect={(item, link = null) => setConnecting({ ...item, currentLink: link })}
            onDisconnect={(item, link) => setConfirmUnlink({ integration: item, link })}
            seoSetup={
              integration.key === "seranking"
                ? { modalOpen: connecting?.key === "seranking" }
                : undefined
            }
          />
        ))}
        {PUBLISHABLE_PLATFORMS.map((platform) => (
          <SocialRow key={platform} platform={platform} social={social} urlField={renderUrlField(platform)} />
        ))}
      </Box>

      {Modal && (
        <Modal
          open
          onOpenChange={(open) => !open && setConnecting(null)}
          clientId={clientId}
          currentLink={connecting.currentLink}
          onConnected={(link) => {
            setLink(connecting.key, link);
            onSuccess?.(`${connecting.label} ${connecting.currentLink ? "actualizado" : "conectado"}`);
          }}
          onDisconnected={() => setLink(connecting.key, null)}
        />
      )}

      <ConfirmModal
        open={Boolean(confirmUnlink)}
        onClose={() => setConfirmUnlink(null)}
        onConfirm={unlink}
        title={confirmUnlink ? `Desconectar ${confirmUnlink.integration.label}` : "Desconectar"}
        description="Los paneles de métricas del cliente dejarán de mostrar estos datos. ¿Seguro que quieres desconectarlo?"
        confirmLabel="Desconectar"
        tone="warning"
      />
    </>
  );
}
